import { Stage } from '../../constants/configurations';
import { getStageFromUrl } from '../../utils/stageUtil';

export const FEDERATE_INTEG_IDENTITY_PROVIDER = 'idp-integ.federate.amazon.com';
export const FEDERATE_PROD_IDENTITY_PROVIDER = 'idp.federate.amazon.com';

// Prod signs in against the prod Federate IdP; every other stage uses the integ IdP. The host is
// both the Federate authorize endpoint and the Cognito `Logins` key, so it must match an OIDC
// provider trusted by that stage's Identity Pool (AWSGCRContentCreationCDK FederateAuthStack).
export const getIdentityProvider = (stage: Stage): string =>
  stage === Stage.PROD ? FEDERATE_PROD_IDENTITY_PROVIDER : FEDERATE_INTEG_IDENTITY_PROVIDER;

export const IDENTITY_PROVIDER = getIdentityProvider(getStageFromUrl());
export const FEDERATE_CLIENT_ID = 'content-connection';
export const FEDERATE_ID_TOKEN = 'federate_id_token';
export const FEDERATE_LOGIN_PATH = '/api/oauth2/v1/authorize';

export const APP_CONFIG = {
  identityProvider: IDENTITY_PROVIDER,
  identityEndpoint: `https://${IDENTITY_PROVIDER}${FEDERATE_LOGIN_PATH}`,
  federateIdToken: FEDERATE_ID_TOKEN,
  federateClientId: FEDERATE_CLIENT_ID,
};

// The portal exchanges the Federate id_token for temporary IAM credentials via a Cognito
// Identity Pool (see CONNECTION_AUTH_CONFIG / getIdentityPoolId) and SigV4-signs Connection
// Management API requests with them; that API is authorized with AWS_IAM.

// Cognito Identity Pools that broker Federate id_token -> temporary IAM credentials.
// `cognitoRegion` is where GetId/GetCredentialsForIdentity are called; `sigv4Region` is the
// region used to SigV4-sign execute-api requests (must match the API Gateway region).
// The China pools live in cn-north-1 (Beijing) because cn-northwest-1 (Ningxia) has no Cognito;
// their creds are still valid for the ZHY execute-api since they share the aws-cn account.
// Pool ids differ per stage and come from each FederateAuthStack CfnOutput
// (ContentConnection-<stage>-IdentityPoolId). Empty string = not yet deployed -> unsigned fallback.
export const CONNECTION_AUTH_CONFIG: Record<
  'global' | 'china',
  {
    cognitoRegion: string;
    sigv4Region: string;
    identityPoolIdByStage: Record<Stage, string>;
    // Per-stage region overrides; a stage falls back to cognitoRegion/sigv4Region when absent.
    regionByStage?: Partial<Record<Stage, { cognitoRegion: string; sigv4Region: string }>>;
  }
> = {
  global: {
    // Default region for stages whose backend is in us-east-1 (gamma/prod).
    cognitoRegion: 'us-east-1',
    sigv4Region: 'us-east-1',
    // Each stage uses the Cognito pool of the backend it connects to (see
    // CONNECTION_MANAGEMENT_ENDPOINTS.global): gamma uses the gamma pool (acct 443370675224),
    // dev/beta use the alpha env (acct 354918398249, us-west-2), prod uses the prod pool (acct 084515815455).
    identityPoolIdByStage: {
      [Stage.DEV]: 'us-west-2:89e65a11-0b80-4cca-9c49-2f8fd198a3bf',
      [Stage.BETA]: 'us-west-2:89e65a11-0b80-4cca-9c49-2f8fd198a3bf',
      [Stage.GAMMA]: 'us-east-1:21545d8b-ed28-4cec-85a9-4ffa6e830787',
      [Stage.PROD]: 'us-east-1:a1cc2e80-464f-46f1-a613-386b14482248',
    },
    // dev/beta connect to the global alpha env, which lives in us-west-2.
    regionByStage: {
      [Stage.DEV]: { cognitoRegion: 'us-west-2', sigv4Region: 'us-west-2' },
      [Stage.BETA]: { cognitoRegion: 'us-west-2', sigv4Region: 'us-west-2' },
    },
  },
  china: {
    cognitoRegion: 'cn-north-1', // Beijing -- Cognito is unavailable in cn-northwest-1
    sigv4Region: 'cn-northwest-1', // API Gateway region
    // Each stage uses the Cognito pool of the backend it connects to (see
    // CONNECTION_MANAGEMENT_ENDPOINTS.china). dev/beta use the beta-cn pool
    // (acct 576297155649), gamma the gamma-cn pool (acct 576299056317), prod the
    // prod-cn pool (acct 576300555274). All pools live in cn-north-1 (see above).
    identityPoolIdByStage: {
      [Stage.DEV]: 'cn-north-1:c7b15e5e-64fc-4a4b-ab4e-9121a13880cf',
      [Stage.BETA]: 'cn-north-1:c7b15e5e-64fc-4a4b-ab4e-9121a13880cf',
      [Stage.GAMMA]: 'cn-north-1:f52b6d31-b4f6-4577-b935-df90e59cf169',
      [Stage.PROD]: 'cn-north-1:59366827-acc4-49db-a804-93910e63f3c7',
    },
  },
};

// Resolve the Cognito Identity Pool id for a region + stage; '' when none is configured yet.
export const getIdentityPoolId = (region: 'global' | 'china', stage: Stage): string =>
  CONNECTION_AUTH_CONFIG[region].identityPoolIdByStage[stage] ?? '';

// Resolve the SigV4 region for a region + stage, honoring per-stage overrides.
export const getSigv4Region = (region: 'global' | 'china', stage: Stage): string =>
  CONNECTION_AUTH_CONFIG[region].regionByStage?.[stage]?.sigv4Region ?? CONNECTION_AUTH_CONFIG[region].sigv4Region;

// Resolve the Cognito region for a region + stage, honoring per-stage overrides.
export const getCognitoRegion = (region: 'global' | 'china', stage: Stage): string =>
  CONNECTION_AUTH_CONFIG[region].regionByStage?.[stage]?.cognitoRegion ?? CONNECTION_AUTH_CONFIG[region].cognitoRegion;
